import { get, isEmpty, has } from "lodash";
import dynamic from "next/dynamic";
import Head from "next/head";
import { Router, useRouter } from "next/router";
import { useStoreState } from "pullstate";
import { useEffect, useState } from "react";
import Store from "../utils/Store";
import { QueryClient, QueryClientProvider } from "react-query";
import { useIdleTimer } from "react-idle-timer";
import Logout from "../components/Logout";
import Menubar from "../components/Menubar";
import { PatientsContextProvider } from "../components/Patients/PatientsContext";
import { GatewayContextProvider } from "../components/GatewayIndicator/GatewayContext";
import { LicenseContextProvider } from "../components/LicenseManager/LicenseContext";
import Toast from "../components/Toast";
import NetworkError from "../components/NetworkError";
import RNEnvSelector from "../components/RNEnvSelector";
import DatadogProvider from "../components/DatadogProvider";
import NotificationSubscription from "../components/NotificationSubscription";
import SettingsModal from "../components/Settings/SettingsModal";
import "../i18n";
import "@qure/qure-auth/styles.css";
import "../styles/globals.css";
import constants from "../utils/constants";
import { getIsOnpremise, isAndroidWebView, isIOSWebView, isWebView } from "../utils";
import "react-image-crop/dist/ReactCrop.css";
import useCheckWorklistContainer from "../hooks/useCheckWorklistContainer";
import getEnv from "../utils/getEnv";
import Warning from "../components/Alert/Warning";
import {
  initializeCountly,
  initMixpanel,
  syncMixpanelEvents,
  trackEvent,
} from "../utils/mixpanel";
import i18next from "i18next";
import { QureAuthProvider } from "@qure/qure-auth";
import { getUserSettings, getAppConfig } from "../services";
import "@copilotkit/react-ui/styles.css";
import PromptsModal from "../components/Assistant/PromptsModal";
import CopilotWrapper from "../components/Assistant/CopilotWrapper";
import { trackCountlyPageView } from "../utils/mixpanel";
import { t } from "i18next";
import TermsAndConditionsModal from "../components/TermsAndConditionsModal";
import AudibleNotifications from "../components/WorkspaceManagement/AudibleNotifications";
import { getBackendURL } from "../utils/axiosConnect";
import Maintenance from "../components/Maintenance";

const isOnpremise = getIsOnpremise();

const ProductFruits =
  !isOnpremise &&
  dynamic(() => import("react-product-fruits").then((mod) => mod.ProductFruits), {
    ssr: false,
  });
Router.events.on("routeChangeComplete", () => {
  window?.productFruits &&
    window?.productFruits?.pageChanged &&
    window?.productFruits?.pageChanged();
});

const PRODUCT_FRUIT_TOKEN = getEnv("NEXT_PUBLIC_PRODUCTFRUITS_WORKSPACE_CODE");

function MyApp({ Component, pageProps }) {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        retry: false,
      },
    },
  });

  const router = useRouter();
  const query = get(router, ["query"], {});
  const onlyViewport = has(query, ["onlyViewport"]);
  const { hideMenu, hasShare } = pageProps;
  const onIdle = () => {
    // Reloading the page when screen is idle after 7.5 Mins
    location.reload();
  };

  let timeout = 1000 * 60 * 7.5;
  if (isWebView()) {
    timeout = 1000 * 60 * 3;
  }
  const idleTimer = useIdleTimer({ onIdle, timeout });
  const isWorkList = useCheckWorklistContainer();

  const { isDarkMode, isRNTestBuild } = useStoreState(Store, (s) => ({
    isDarkMode: s.isDarkMode,
    isRNTestBuild: s.isRNTestBuild,
  }));

  const [isPromptsModalOpen, setIsPromptsModalOpen] = useState(false);
  const [isTermsAndConditionsModalOpen, setIsTermsAndConditionsModalOpen] =
    useState(false);

  useEffect(() => {
    if (window) {
      // const params = queryString.parse(window?.location?.search);
      // if (params["redirectLogin"]) {
      //   localStorage.setItem("redirectLogin", true);
      // }
      // becasue useMedia hook does not work in _app.js, so we need to check the screen size manually
      const isMobile = window.matchMedia("(max-width: 639px)")?.matches;
      let _isDarkMode = JSON.parse(localStorage.getItem(constants.PERSISTED.isDarkMode));
      // to set default dark mode
      if (_isDarkMode === null) {
        _isDarkMode =
          getEnv("NEXT_PUBLIC_USE_LIGHT_THEME") === "true" && !isMobile ? false : true;
      }
      Store.update((s) => {
        s.isDarkMode = _isDarkMode;
      });
    }
  }, []);

  useEffect(() => {
    if (isDarkMode) {
      document.querySelector("body").classList.add("dark");
    } else {
      document.querySelector("body").classList.remove("dark");
    }
  }, [isDarkMode]);

  useEffect(() => {
    // Using web workers to pass data from Android and iOS webviews
    if (isIOSWebView() || isAndroidWebView()) {
      window.addEventListener("message", onRNMessage); // for iOS
      document.addEventListener("message", onRNMessage); // for Android
      window?.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: constants.RN_COMM_CONST.APP_READY,
        })
      );
    }

    return () => {
      window.removeEventListener("message", onRNMessage);
      document.removeEventListener("message", onRNMessage);
    };
  }, []);

  const onRNMessage = (e) => {
    const nativeData = get(e, ["data"], "");
    if (nativeData) {
      try {
        const messageData = JSON.parse(nativeData);
        Store.update((store) => {
          store.isRNTestBuild = get(messageData, ["isRNTestBuild"], store.isRNTestBuild);
          store.RNSelectedEnv = get(messageData, ["env"], store.RNSelectedEnv);
          store.RNfcmToken = get(messageData, ["fcmToken"], store.RNfcmToken);
          store.isRNAppStateChange = get(messageData, ["isRNAppStateChange"], false);
          store.RNAppVersion = get(messageData, ["AppVersion"], store.RNAppVersion);
        });
      } catch (e) {
        console.error(e);
      }
    }
  };

  useEffect(() => {
    // Check the screen width
    const screenWidth = window.innerWidth;

    if (screenWidth > 550) {
      window.onUsersnapCXLoad = function (api) {
        api.init({
          user: {
            app: "qure",
          },
        });
      };
    }

    // Dynamically create a script element
    const scriptElement = document.createElement("script");

    if (screenWidth > 550) {
      scriptElement.defer = 1;
      scriptElement.src =
        "https://widget.usersnap.com/global/load/1fec3ff9-9d2e-4d2e-a6b9-74cabf1f548e?onload=onUsersnapCXLoad";
    }
    // Append the script to the document head
    document.getElementsByTagName("head")[0].appendChild(scriptElement);

    // Clean up: remove the script element when the component unmounts
    return () => {
      document.getElementsByTagName("head")[0].removeChild(scriptElement);
    };
  }, []);

  const trackOTPLogin = () => {
    const otpLoginSuccess = localStorage.getItem(constants.PERSISTED.otpLoginSuccess);
    if (otpLoginSuccess === "true") {
      localStorage.removeItem(constants.PERSISTED.otpLoginSuccess);
      trackEvent(MIXPANEL_EVENTS.OTP_LOGIN_SUCCESS);
    }
  };

  const onAuthSuccess = (userInfo, shouldRedirectToApp) => {
    // localStorage.removeItem("redirectLogin");
    window["userInfo"] = userInfo;

    if (window?.ReactNativeWebView) {
      window?.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: constants.RN_COMM_CONST.FLUSH_COOKIES,
        })
      );
    }
    if (window.electronAPI) {
      window.electronAPI.setAPISettings({
        apiURL: getEnv("NEXT_PUBLIC_API_URL"),
        token: userInfo?.token || null,
      });
    }

    i18next.changeLanguage(
      get(userInfo, ["settings", "app_language"], navigator.language)
    );

    Store.update((store) => {
      store.addPhoneModal = userInfo?.request_phone_number;
      store.isqTrack = userInfo?.settings?.app_logo === "qtrack";
    });

    getUserSettings();
    getAppConfig();
    initMixpanel()(userInfo);
    initializeCountly(userInfo)();
    trackOTPLogin();
    syncMixpanelEvents();

    // === TO BE DISCUSSED ===
    const currentVersion = constants.TERMS_OF_USE_VERSION;
    // WARNING: stop gap solution. must be reverted in next release
    const isCurrentVersion = userInfo?.agreement?.version === currentVersion;
    if ((!userInfo || isCurrentVersion) && router.pathname === "/terms-and-conditions") {
      router.push("/");
      return;
    }

    if (!isCurrentVersion && router.pathname !== "/open-app") {
      setIsTermsAndConditionsModalOpen(true);
    }

    if (localStorage.getItem(constants.PERSISTED.requestedWorkspaceData)) {
      router.replace("/workspace/send-request");
    } else if (!userInfo?.has_source_access && isEmpty(userInfo?.pending_requests)) {
      router.replace("/workspace/join");
    } else if (!userInfo?.has_source_access && !isEmpty(userInfo?.pending_requests)) {
      router.replace("/workspace/pending");
    } else if (shouldRedirectToApp) {
      redirectToApp();
    }
  };

  useEffect(() => {
    const handleHashChange = () => {
      setIsPromptsModalOpen(window.location.hash === "#prompts");
    };

    // Check hash on initial load
    handleHashChange();

    // Hide powered by element if it exists
    const poweredByElement = document.querySelector(".poweredBy");
    if (poweredByElement) {
      poweredByElement.style.setProperty("display", "none", "important");
    }
    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);

    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const handleClosePromptsModal = () => {
    setIsPromptsModalOpen(false);
    // Remove the hash from the URL without causing a page reload
    if (window.history.pushState) {
      window.history.pushState(
        "",
        "/",
        window.location.pathname + window.location.search
      );
    } else {
      window.location.hash = "";
    }
  };

  useEffect(() => {
    // Track Countly page view only for /viewer route
    if (router.pathname.startsWith("/viewer")) {
      trackCountlyPageView("VIEWER", { patient_id: router?.query?.params?.[1] });
    }
  }, [router.pathname]);

  const redirectToApp = () => {
    router.replace("/open-app");
  };

  return (
    <>
      <Head>
        <meta charSet="UTF-8" />
        <meta property="og:site_name" content="Qure.ai" />
        <meta name="theme-color" content={isWorkList ? "#29333E" : "#0C1723"} />

        <meta
          name="description"
          content="View imaging studies and collaborate in real-time"
        />
        <meta
          property="og:description"
          content="View imaging studies and collaborate in real-time"
        />

        <meta property="og:image:width" content="1070" />
        <meta property="og:image:height" content="580" />
        <meta property="og:type" content="website" />

        <meta name="keywords" content="qure, qer, qxr, stroke" />
        <meta name="author" content="Qure.ai" />
        <meta
          name="viewport"
          content="minimum-scale=1, initial-scale=1, width=device-width, shrink-to-fit=no, user-scalable=no"
        />
      </Head>
      {hasShare ? (
        <Head>
          <meta property="og:title" content="Study shared with you via Qure App" />
          <meta
            property="og:image"
            itemProp="image"
            content="https://app.qure.ai/assets/images/sharingPreview.png"
          />
        </Head>
      ) : (
        <Head>
          <meta property="og:title" content="Qure.ai" />
          <meta
            property="og:image"
            itemProp="image"
            content="https://app.qure.ai/assets/images/pageMeta.png"
          />
        </Head>
      )}
      <QueryClientProvider client={queryClient} contextSharing={true}>
        <QureAuthProvider
          onAuthSuccess={onAuthSuccess}
          appLogoUrl={"/assets/images/qureLogo.svg"}
          regions={getEnv("NEXT_PUBLIC_REGIONS")}
          isOnpremise={isOnpremise}
          isWebView={isWebView()}
          t={t}
          allowRegistration={getEnv("NEXT_PUBLIC_ALLOW_REGISTRATION") === "true"}
          apiBaseUrl={getBackendURL()}
          redirectToLogin={() => {
            setIsTermsAndConditionsModalOpen(false);
            window.location.href = "/landing-page";
          }}
        >
          {(userInfo) => {
            const isLoggedIn = get(userInfo, ["authenticated"], false);
            const user = get(userInfo, ["details"], null);
            return (
              <DatadogProvider userInfo={userInfo}>
                <CopilotWrapper authData={userInfo}>
                  <Maintenance />
                  <div className="h-full flex bg-gray-100 dark:bg-gray-900 sm:bg-gray-100 sm:dark:bg-gray-900">
                    {!isOnpremise &&
                      global.window &&
                      isLoggedIn &&
                      !!PRODUCT_FRUIT_TOKEN && (
                        <ProductFruits
                          workspaceCode={PRODUCT_FRUIT_TOKEN}
                          language="en"
                          user={{
                            firstname: get(user, ["first_name"], ""),
                            lastname: get(user, ["last_name"], ""),
                            email: get(user, ["email"], ""),
                          }}
                          config={{ disableLocationChangeDetection: true }}
                        />
                      )}
                    {!isOnpremise && !onlyViewport && <NotificationSubscription />}
                    <PatientsContextProvider>
                      <LicenseContextProvider>
                        <GatewayContextProvider>
                          <div className="flex flex-col w-full h-full overflow-hidden">
                            {!hideMenu && !onlyViewport && <Menubar />}
                            {!hideMenu && <Warning />}
                            <Component {...pageProps} />
                          </div>
                          <SettingsModal />
                          <AudibleNotifications />
                        </GatewayContextProvider>
                      </LicenseContextProvider>
                    </PatientsContextProvider>
                    <PromptsModal
                      isOpen={isPromptsModalOpen}
                      onClose={handleClosePromptsModal}
                    />
                    <Logout />
                    <Toast />
                    {!isOnpremise && <NetworkError />}
                    {!isOnpremise && isRNTestBuild && <RNEnvSelector />}
                  </div>
                  {isTermsAndConditionsModalOpen && (
                    <TermsAndConditionsModal
                      closeOnClickAway={false}
                      showModal={isTermsAndConditionsModalOpen}
                      onAgree={() => {
                        setIsTermsAndConditionsModalOpen(false);
                      }}
                      mode="interactive"
                    />
                  )}
                </CopilotWrapper>
              </DatadogProvider>
            );
          }}
        </QureAuthProvider>
      </QueryClientProvider>
    </>
  );
}

export default MyApp;
